import React, { useCallback } from 'react';
import type { Feature } from 'geojson';

import { buildCircleRing } from '../../MapView/geo';

type Marker = { id: string; label: string; coordinate: [number, number] };

export type SmartSearchSelection = {
  id: string;
  type:
    | 'COORDINATE'
    | 'SITE'
    | 'CADASTRAL_AREA'
    | 'MUNICIPALITY'
    | 'VILLAGE';
  label: string;
  detail: string;
  longitude: number;
  latitude: number;
  bounds: [number, number, number, number] | null;
  geometry: Feature['geometry'] | null;
  score: number;
};

export enum MapTools {
  ADD_MARKER_POINT_TOOL = 'ADD_MARKER_POINT_TOOL',
  OBJECT_SELECT = 'OBJECT_SELECT',
  OBJECT_BOX_SELECT = 'OBJECT_BOX_SELECT',
  OBJECT_DRAW_POINT = 'OBJECT_DRAW_POINT',
  OBJECT_DRAW_LINE = 'OBJECT_DRAW_LINE',
  OBJECT_DRAW_POLYGON = 'OBJECT_DRAW_POLYGON',
  OBJECT_DRAW_CIRCLE = 'OBJECT_DRAW_CIRCLE',
}

export const OBJECT_DRAW_TOOLS = new Set<MapTools>([
  MapTools.OBJECT_DRAW_POINT,
  MapTools.OBJECT_DRAW_LINE,
  MapTools.OBJECT_DRAW_POLYGON,
  MapTools.OBJECT_DRAW_CIRCLE,
]);

export const OBJECT_TOOLS = new Set<MapTools>([
  MapTools.OBJECT_SELECT,
  MapTools.OBJECT_DRAW_POINT,
  MapTools.OBJECT_DRAW_LINE,
  MapTools.OBJECT_DRAW_POLYGON,
  MapTools.OBJECT_DRAW_CIRCLE,
]);

type WorkspaceStoreState = {
  activeMapTool: MapTools | null;
  markerPoints: Marker[];
  sharedInitialCoordinates: string;
  shareMapDialogOpen: boolean;
  loadedShareSlug: string | null;
  objectDraft: Feature | null;
  editingObjectId: string | null;
  objectGeometryDirty: boolean;
  originalEditGeometry: string | null;
  objectSnappingEnabled: boolean;
  batchSelectedIds: string[];
  smartSearchSelection: SmartSearchSelection | null;
};

enum WorkspaceActionType {
  SET_ACTIVE_MAP_TOOL = 'SET_ACTIVE_MAP_TOOL',
  ADD_MARKER_POINT = 'ADD_MARKER_POINT',
  SET_SHARED_MAP = 'SET_SHARED_MAP',
  DELETE_MARKER_POINT = 'DELETE_MARKER_POINT',
  SET_SHARE_MAP_DIALOG_OPEN = 'SET_SHARE_MAP_DIALOG_OPEN',
  CLEAR_MARKER_POINTS = 'CLEAR_MARKER_POINTS',
  SET_LOADED_SHARE_SLUG = 'SET_LOADED_SHARE_SLUG',
  START_DRAW_OBJECT = 'START_DRAW_OBJECT',
  START_EDIT_OBJECT = 'START_EDIT_OBJECT',
  SET_OBJECT_DRAFT = 'SET_OBJECT_DRAFT',
  APPEND_DRAFT_POINT = 'APPEND_DRAFT_POINT',
  CLEAR_OBJECT_DRAFT = 'CLEAR_OBJECT_DRAFT',
  TOGGLE_OBJECT_SNAPPING = 'TOGGLE_OBJECT_SNAPPING',
  START_BOX_SELECT = 'START_BOX_SELECT',
  SET_BATCH_SELECTED_IDS = 'SET_BATCH_SELECTED_IDS',
  CLEAR_BATCH_SELECTION = 'CLEAR_BATCH_SELECTION',
  SET_SMART_SEARCH_SELECTION = 'SET_SMART_SEARCH_SELECTION',
}

type WorkspaceStoreAction = {
  type: WorkspaceActionType;
  payload?: any;
};

const workspaceReducer = (
  state: WorkspaceStoreState,
  action: WorkspaceStoreAction
) => {
  switch (action.type) {
    case 'ADD_MARKER_POINT':
      const newMarker = {
        id: new Date().toISOString(),
        ordinal: state.markerPoints.length + 1,
        ...action.payload,
      };
      return {
        ...state,
        markerPoints: [...state.markerPoints, newMarker],
        loadedShareSlug: null,
      };
    case 'SET_SHARED_MAP':
      return {
        ...state,
        sharedInitialCoordinates: action.payload.sharedInitialCoordinates,
        loadedShareSlug:
          action.payload.loadedShareSlug !== undefined
            ? action.payload.loadedShareSlug
            : state.loadedShareSlug,
        markerPoints: (action.payload.markerPoints || state.markerPoints).map(
          (m, i) => ({
            ...m,
            ordinal: i + 1,
          })
        ),
      };
    case 'DELETE_MARKER_POINT':
      const updatedPoints = state.markerPoints
        .filter((m) => m.id !== action.payload)
        .map((m, index) => ({ ...m, ordinal: index + 1 }));
      return {
        ...state,
        markerPoints: updatedPoints,
        loadedShareSlug: null,
      };
    case 'CLEAR_MARKER_POINTS':
      return {
        ...state,
        markerPoints: [],
        activeMapTool: null,
        loadedShareSlug: null,
      };
    case 'SET_ACTIVE_MAP_TOOL':
      return {
        ...state,
        activeMapTool: action.payload,
      };
    case 'SET_SHARE_MAP_DIALOG_OPEN':
      return {
        ...state,
        shareMapDialogOpen: action.payload,
      };
    case 'SET_LOADED_SHARE_SLUG':
      return {
        ...state,
        loadedShareSlug: action.payload,
      };
    case 'START_DRAW_OBJECT':
      return {
        ...state,
        activeMapTool: action.payload as MapTools,
        editingObjectId: null,
        objectDraft: null,
        objectGeometryDirty: false,
        batchSelectedIds: [],
      };
    case 'START_EDIT_OBJECT': {
      const feature = action.payload.feature ?? null;
      return {
        ...state,
        activeMapTool: MapTools.OBJECT_SELECT,
        editingObjectId: action.payload.id,
        objectDraft: feature,
        objectGeometryDirty: false,
        originalEditGeometry: feature?.geometry
          ? JSON.stringify(feature.geometry)
          : null,
        batchSelectedIds: [],
      };
    }
    case 'START_BOX_SELECT':
      return {
        ...state,
        activeMapTool: MapTools.OBJECT_BOX_SELECT,
        editingObjectId: null,
        objectDraft: null,
        objectGeometryDirty: false,
        originalEditGeometry: null,
        batchSelectedIds: [],
      };
    case 'SET_BATCH_SELECTED_IDS':
      return {
        ...state,
        batchSelectedIds: action.payload as string[],
      };
    case 'CLEAR_BATCH_SELECTION':
      return {
        ...state,
        batchSelectedIds: [],
      };
    case 'SET_SMART_SEARCH_SELECTION':
      return {
        ...state,
        smartSearchSelection: action.payload,
      };
    case 'SET_OBJECT_DRAFT': {
      const nextGeometry = action.payload?.geometry;
      const geometryDirty =
        !!state.editingObjectId &&
        !!nextGeometry &&
        (state.originalEditGeometry == null
          ? true
          : JSON.stringify(nextGeometry) !== state.originalEditGeometry);
      return {
        ...state,
        objectDraft: action.payload,
        objectGeometryDirty: geometryDirty,
      };
    }
    case 'APPEND_DRAFT_POINT': {
      const coord = action.payload as [number, number];
      const draft = state.objectDraft;
      // No draft yet: initialize based on active tool
      if (!draft) {
        const tool = state.activeMapTool;
        if (tool === MapTools.OBJECT_DRAW_POINT) {
          return {
            ...state,
            objectDraft: {
              type: 'Feature',
              properties: {},
              geometry: { type: 'Point', coordinates: coord },
            },
          };
        }
        if (
          tool === MapTools.OBJECT_DRAW_LINE ||
          tool === MapTools.OBJECT_DRAW_POLYGON
        ) {
          return {
            ...state,
            objectDraft: {
              type: 'Feature',
              properties: {},
              // Start as LineString until we have >=3 points and the tool is
              // polygon (handled below). Simplifies incremental display.
              geometry: { type: 'LineString', coordinates: [coord] },
            },
          };
        }
        if (tool === MapTools.OBJECT_DRAW_CIRCLE) {
          return {
            ...state,
            objectDraft: {
              type: 'Feature',
              properties: { _circleCenter: coord },
              geometry: { type: 'Point', coordinates: coord },
            },
          };
        }
        return state;
      }
      // Draft already exists: extend based on geometry type + active tool
      const geom = draft.geometry as any;
      const tool = state.activeMapTool;
      if (tool === MapTools.OBJECT_DRAW_POINT) {
        // Replace the point on subsequent clicks
        return {
          ...state,
          objectDraft: {
            ...draft,
            geometry: { type: 'Point', coordinates: coord },
          },
        };
      }
      if (tool === MapTools.OBJECT_DRAW_LINE) {
        return {
          ...state,
          objectDraft: {
            ...draft,
            geometry: {
              type: 'LineString',
              coordinates: [...(geom.coordinates as [number, number][]), coord],
            },
          },
        };
      }
      if (tool === MapTools.OBJECT_DRAW_POLYGON) {
        // Accumulate a flat coordinate list under LineString; on save it will
        // be closed into a Polygon.
        const currentCoords: [number, number][] =
          geom.type === 'LineString'
            ? geom.coordinates
            : (geom.coordinates[0] as [number, number][]).slice(0, -1);
        const nextCoords = [...currentCoords, coord];
        if (nextCoords.length < 3) {
          return {
            ...state,
            objectDraft: {
              ...draft,
              geometry: { type: 'LineString', coordinates: nextCoords },
            },
          };
        }
        return {
          ...state,
          objectDraft: {
            ...draft,
            geometry: {
              type: 'Polygon',
              coordinates: [[...nextCoords, nextCoords[0]]],
            },
          },
        };
      }
      if (tool === MapTools.OBJECT_DRAW_CIRCLE) {
        const center = (draft.properties?._circleCenter ?? geom.coordinates) as
          | [number, number]
          | undefined;
        if (!center) return state;
        const ring = buildCircleRing(center, coord, 64);
        return {
          ...state,
          objectDraft: {
            ...draft,
            properties: { ...(draft.properties ?? {}), _circleCenter: center },
            geometry: { type: 'Polygon', coordinates: [ring] },
          },
        };
      }
      return state;
    }
    case 'CLEAR_OBJECT_DRAFT':
      return {
        ...state,
        objectDraft: null,
        editingObjectId: null,
        objectGeometryDirty: false,
        originalEditGeometry: null,
      };
    case 'TOGGLE_OBJECT_SNAPPING':
      return {
        ...state,
        objectSnappingEnabled: !state.objectSnappingEnabled,
      };
    default:
      return state;
  }
};

function createCtx(defaultValue: WorkspaceStoreState) {
  const ctx = React.createContext({
    store: defaultValue,
    hoveredMarkerId: null as string | null,
    setActiveMapTool: null,
    setSharedMap: null,
    setLoadedShareSlug: null,
    addMarkerPoint: null,
    deleteMarkerPoint: null,
    onShareMap: null,
    onShareMapDialogClose: null,
    clearMarkerPoints: null,
    setHoveredMarkerId: null,
    startDrawObject: null,
    startEditObject: null,
    setObjectDraft: null,
    appendDraftPoint: null,
    clearObjectDraft: null,
    toggleObjectSnapping: null,
    startBoxSelect: null,
    setBatchSelectedIds: null,
    clearBatchSelection: null,
    setSmartSearchSelection: null,
    getWorkspaceStore: null,
  });

  function WorkspaceProvider(props: React.PropsWithChildren<{}>) {
    const [store, dispatchBase] = React.useReducer(workspaceReducer, defaultValue);
    const storeRef = React.useRef(store);
    const [hoveredMarkerId, setHoveredMarkerId] = React.useState<string | null>(
      null
    );

    const dispatch = useCallback((action: WorkspaceStoreAction) => {
      storeRef.current = workspaceReducer(storeRef.current, action);
      dispatchBase(action);
    }, []);

    const getWorkspaceStore = useCallback(() => storeRef.current, []);

    const setActiveMapTool = useCallback(
      (options) => {
        dispatch({
          type: WorkspaceActionType.SET_ACTIVE_MAP_TOOL,
          payload: options,
        });
      },
      [dispatch]
    );

    const addMarkerPoint = useCallback(
      (options) => {
        dispatch({
          type: WorkspaceActionType.ADD_MARKER_POINT,
          payload: options,
        });
      },
      [dispatch]
    );

    const clearMarkerPoints = useCallback(() => {
      setHoveredMarkerId(null);
      dispatch({
        type: WorkspaceActionType.CLEAR_MARKER_POINTS,
        payload: [],
      });
    }, [dispatch]);

    const setSharedMap = useCallback(
      (options) => {
        dispatch({
          type: WorkspaceActionType.SET_SHARED_MAP,
          payload: options,
        });
      },
      [dispatch]
    );

    const setLoadedShareSlug = useCallback(
      (slug: string | null) => {
        dispatch({
          type: WorkspaceActionType.SET_LOADED_SHARE_SLUG,
          payload: slug,
        });
      },
      [dispatch]
    );

    const deleteMarkerPoint = useCallback(
      (id: string) => {
        setHoveredMarkerId((current) => (current === id ? null : current));
        dispatch({
          type: WorkspaceActionType.DELETE_MARKER_POINT,
          payload: id,
        });
      },
      [dispatch]
    );

    const onShareMap = useCallback(() => {
      dispatch({
        type: WorkspaceActionType.SET_SHARE_MAP_DIALOG_OPEN,
        payload: true,
      });
    }, [dispatch]);

    const onShareMapDialogClose = useCallback(() => {
      dispatch({
        type: WorkspaceActionType.SET_SHARE_MAP_DIALOG_OPEN,
        payload: false,
      });
    }, [dispatch]);

    const startDrawObject = useCallback(
      (tool: MapTools) => {
        dispatch({
          type: WorkspaceActionType.START_DRAW_OBJECT,
          payload: tool,
        });
      },
      [dispatch]
    );

    const startEditObject = useCallback(
      (id: string, feature: Feature | null) => {
        dispatch({
          type: WorkspaceActionType.START_EDIT_OBJECT,
          payload: { id, feature },
        });
      },
      [dispatch]
    );

    const setObjectDraft = useCallback(
      (feature: Feature | null) => {
        dispatch({
          type: WorkspaceActionType.SET_OBJECT_DRAFT,
          payload: feature,
        });
      },
      [dispatch]
    );

    const appendDraftPoint = useCallback(
      (coord: [number, number]) => {
        dispatch({
          type: WorkspaceActionType.APPEND_DRAFT_POINT,
          payload: coord,
        });
      },
      [dispatch]
    );

    const clearObjectDraft = useCallback(() => {
      dispatch({ type: WorkspaceActionType.CLEAR_OBJECT_DRAFT });
    }, [dispatch]);

    const toggleObjectSnapping = useCallback(() => {
      dispatch({ type: WorkspaceActionType.TOGGLE_OBJECT_SNAPPING });
    }, [dispatch]);

    const startBoxSelect = useCallback(() => {
      dispatch({ type: WorkspaceActionType.START_BOX_SELECT });
    }, [dispatch]);

    const setBatchSelectedIds = useCallback(
      (ids: string[]) => {
        dispatch({
          type: WorkspaceActionType.SET_BATCH_SELECTED_IDS,
          payload: ids,
        });
      },
      [dispatch]
    );

    const clearBatchSelection = useCallback(() => {
      dispatch({ type: WorkspaceActionType.CLEAR_BATCH_SELECTION });
    }, [dispatch]);

    const setSmartSearchSelection = useCallback(
      (selection: SmartSearchSelection | null) => {
        dispatch({
          type: WorkspaceActionType.SET_SMART_SEARCH_SELECTION,
          payload: selection,
        });
      },
      [dispatch]
    );

    const value = React.useMemo(
      () => ({
        store,
        hoveredMarkerId,
        setHoveredMarkerId,
        addMarkerPoint,
        deleteMarkerPoint,
        clearMarkerPoints,
        setActiveMapTool,
        setSharedMap,
        setLoadedShareSlug,
        onShareMap,
        onShareMapDialogClose,
        startDrawObject,
        startEditObject,
        setObjectDraft,
        appendDraftPoint,
        clearObjectDraft,
        toggleObjectSnapping,
        startBoxSelect,
        setBatchSelectedIds,
        clearBatchSelection,
        setSmartSearchSelection,
        getWorkspaceStore,
      }),
      [
        store,
        hoveredMarkerId,
        addMarkerPoint,
        deleteMarkerPoint,
        clearMarkerPoints,
        setActiveMapTool,
        setSharedMap,
        setLoadedShareSlug,
        onShareMap,
        onShareMapDialogClose,
        startDrawObject,
        startEditObject,
        setObjectDraft,
        appendDraftPoint,
        clearObjectDraft,
        toggleObjectSnapping,
        startBoxSelect,
        setBatchSelectedIds,
        clearBatchSelection,
        setSmartSearchSelection,
        getWorkspaceStore,
      ]
    );

    return <ctx.Provider value={value} {...props} />;
  }

  return [ctx, WorkspaceProvider] as [typeof ctx, typeof WorkspaceProvider];
}

const [WorkspaceContext, WorkspaceProvider] = createCtx({
  activeMapTool: null,
  markerPoints: [],
  sharedInitialCoordinates: null,
  shareMapDialogOpen: false,
  loadedShareSlug: null,
  objectDraft: null,
  editingObjectId: null,
  objectGeometryDirty: false,
  originalEditGeometry: null,
  objectSnappingEnabled: true,
  batchSelectedIds: [],
  smartSearchSelection: null,
});

export { WorkspaceContext, WorkspaceProvider };
